import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { Checkbox } from "antd";
import { UseGetStoreData } from "../../../hooks/getStoreData/UseGetStoreData";
import { BackgroundWrapper } from "../../atoms/backgroundWrapper/BackgroundWrapper";
import { Container } from "../../atoms/container/Container";
import { Text } from "../../atoms/text/Text";
import { Input } from "../../atoms/input/Input";
import { AddressCard } from "../../molecules/addressCard/AddressCard";
import { ShareProfileButton } from "../../molecules/shareProfileButton/ShareProfileButton";
import { sendWhatsappMessage, updateTicket } from "../../../pages/actions/actions";
import { ShowAlert } from "../../../utils/showAlert";
import { debounce } from "../../../utils/helper";
import { useEmergencyContact } from "../../../hooks/useEmergencyContact/UseEmergencyContact";
import useBenDataByField from "../../../hooks/useBenDataByField/useBenDatabyField";
import { searchHospitals } from "../../../pages/actions/actions";
import style from "./nearByHospital.module.scss";
import { ticketDataSuccess } from "../../../redux/ticketSlice/ticketSlice";
import { useDispatch } from "react-redux";
// Types
interface Insurance {
  nameOfInsurer: string;
  isBlackListed?: boolean | null;
  isCashless?: boolean | null;
}

interface Hospital {
  id: string;
  name: string;
  address: string;
  handleEmergency?: boolean;
  insuranceList?: Insurance[];
  distance?: number;
  isGovernmentHospital?: boolean;
  isEsic?: boolean;
}

interface FilterState {
  E: boolean; // Emergency
  N: boolean; // Network
  ESIC: boolean; // ESIC
}

interface NearByHospitalProps {
  handleAddressSelection: (hospital: Hospital) => void;
  selectedHospital: Hospital | null;
  nearByHospital: Hospital[];
  pickupLocation: any;
  component?: string;
  km: number;
}

const SEARCH_DEBOUNCE_DELAY = 500;
const MAX_HOSPITALS_TO_SHARE = 3;

const getBadgeText = (hospital: Hospital, insurerNamesSet: Set<string>): string[] => {
  let badgeArray = new Array<string>();
  const isEmergency = hospital?.handleEmergency;
  let isBlackListed = false;
  let isCashless = false;
  const isNetwork = hospital.insuranceList?.some((insurer: Insurance) => {
    if (insurerNamesSet.has(insurer?.nameOfInsurer)) {
      if (insurer.isBlackListed) isBlackListed = true;
      if (insurer.isCashless) isCashless = true;
      return true;
    }
    return false;
  });

  // if (isEmergency && isNetwork) return "E & N";
  if (isEmergency) badgeArray.push("E");
  if (isNetwork && isCashless) badgeArray.push("NL");
  if (isNetwork && !isCashless) badgeArray.push("NC");
  // if (isNetwork) badgeArray.push("N");
  if (isBlackListed) badgeArray.push("B");
  if (hospital?.isEsic) badgeArray.push("ESIC");
  return badgeArray;
};

export const NearByHospital: React.FC<NearByHospitalProps> = ({
  handleAddressSelection,
  selectedHospital,
  nearByHospital,
  component,
  pickupLocation,
  km
}) => {
  const { validContacts } = useEmergencyContact();
  const { getTicketUpdateData, getTicketData } = UseGetStoreData();
  const { data: benInsuranceData } = useBenDataByField({
    patient_id: getTicketData?.patient?.id || getTicketUpdateData?.patient?.id,
    field: "insurancePolicies"
  });
  const dispatch = useDispatch();

  const [isBtnLoading, setIsBtnLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState<Hospital[]>([]);
  const [filters, setFilters] = useState<FilterState>({ E: false, N: false, ESIC: false });
  const abortControllerRef = useRef<AbortController | null>(null);
  const insurerNamesSet: Set<string> = useMemo(() => {
    const fromInsurance = new Set<string>(
      benInsuranceData
        ?.filter((insurance: any) => insurance?.nameOfInsurer && insurance.nameOfInsurer !== "N/A")
        ?.map((insurance: any) => insurance?.nameOfInsurer) ?? []
    );
    if (getTicketData?.companyName === "Care Insurance") {
      fromInsurance.add("Care Insurance");
    }
    return fromInsurance;
  }, [benInsuranceData, getTicketData?.companyName]);

  const hasActiveFilters = filters.E || filters.N || filters.ESIC;

  const displayHospitals = useMemo(() => {
    // If we have search results, show them
    if (searchResults.length > 0) return searchResults;
    
    // If we have active search or filters but no results, show empty
    if (searchValue.trim() || hasActiveFilters) return [];
    
    // Otherwise show default nearby hospitals
    return nearByHospital;
  }, [searchResults, nearByHospital, searchValue, hasActiveFilters]);

  const performSearch = useCallback(async (searchTerm: string) => {
    const trimmedSearch = searchTerm.trim();

    try {
      setIsSearching(true);

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const pickupLat = pickupLocation?.patientLocation?.geoLocation?.coordinates[0] || 0;
      const pickupLng = pickupLocation?.patientLocation?.geoLocation?.coordinates[1] || 0;
      
      const payload: any = {
        name: trimmedSearch,
        PickupLatitude: pickupLat,
        PickupLongitude: pickupLng
      };

      // Only add radius if no search term
      if (!trimmedSearch) {
        payload.radius = km * 1000;
      }

      if (filters.ESIC) payload.isEsic = true;
      if (filters.E) payload.handleEmergency = true;
      if (filters.N) {
        if (insurerNamesSet.size === 0) {
          setSearchResults([]);
          return;
        }
        payload.insuranceCompanies = Array.from(insurerNamesSet);
      }

      const response = await searchHospitals(payload, controller);
      const results = response?.data;

      if (results?.length === 0) {
        setSearchResults([]);
      } else {
        const filteredData = results.sort((a: Hospital, b: Hospital) => {
          const aDist = a.distance ?? Infinity;
          const bDist = b.distance ?? Infinity;

          if (aDist <= 25 && bDist <= 25) {
            if (a.handleEmergency && !b.handleEmergency) return -1;
            if (!a.handleEmergency && b.handleEmergency) return 1;
          }

          return aDist - bDist;
        });
        setSearchResults(filteredData);
      }

    } catch (error: any) {
      if (error?.name === 'CanceledError') {
        // Request was cancelled, do nothing
      } else {
        setSearchResults([]);
      }
    } finally {
      setIsSearching(false);
    }
  }, [pickupLocation, filters, insurerNamesSet, km]);

  const performSearchRef = useRef(performSearch);
  useEffect(() => {
    performSearchRef.current = performSearch;
  }, [performSearch]);

  const debouncedSearch = useMemo(
    () => debounce((term: string) => performSearchRef.current(term), SEARCH_DEBOUNCE_DELAY),
    []
  );

  useEffect(() => {
    // Clear search results when search is empty and no filters
    if (!searchValue && !hasActiveFilters) {
      setSearchResults([]);
      return;
    }

    debouncedSearch(searchValue);
  }, [searchValue, hasActiveFilters, filters, km, pickupLocation, insurerNamesSet, debouncedSearch]);

  const toggleFilter = useCallback((key: keyof FilterState) => {
    setFilters(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const handleClick = useCallback(async () => {
    const patientAddress = getTicketUpdateData?.patientLocation?.address;
    if (!getTicketData?.patient && getTicketUpdateData?.patient) {
      try {
        const res = await updateTicket(getTicketData._id, { patient: getTicketUpdateData.patient });
        if (res) {
          dispatch(ticketDataSuccess(res.data));
        }
      } catch (error) {
        console.error('Error updating patient:', error);
      }
    }

    const hospitalsToShare = nearByHospital.slice(0, MAX_HOSPITALS_TO_SHARE);

    if (!patientAddress || hospitalsToShare.length === 0) {
      ShowAlert("warning", "Patient address not found or hospital not found");
      return;
    }
    setIsBtnLoading(true);

    try {
      await Promise.all(
        hospitalsToShare.map((hospital) =>
          sendWhatsappMessage({
            template: "NEARBY_HOSPITAL_V2",
            data: {
              current_address: patientAddress,
              hospital_name: hospital.name,
              hospital_address: hospital.address,
              to: [...validContacts]
            }
          })
        )
      );
      ShowAlert("success", "Hospital data shared");
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Technical issue in sending WhatsApp message";
      ShowAlert("warning", errorMessage);
    } finally {
      setIsBtnLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getTicketUpdateData?.patientLocation?.address, nearByHospital, validContacts]);

  const displayMessage = useMemo(() => {
    const count = displayHospitals?.length;
    const hasSearch = searchValue.trim().length > 0;

    if (count === 0 && hasSearch) {
      return "No hospitals found for the search";
    }

    if (hasSearch) {
      return `Found ${count} hospitals matching your search`;
    }

    return `We can see ${count} hospitals at ${km} km`;
  }, [displayHospitals.length, searchValue, km]);

  return (
    <BackgroundWrapper className={style.background}>
      <Container>
        <Container className={component ? style.heading_flex : style.flex_header}>
          <div className={style.filter}>
            <Text className={style.title} text="Select Drop Location" />
            <div
              className={`${style.badge} ${filters.E ? style.active : ""}`}
              onClick={() => toggleFilter('E')}
            >
              <Checkbox checked={filters.E} />
              <Text className={style.badge_text} text="Handle Emergency (E)" />
            </div>
            <div
              className={`${style.badge} ${filters.N ? style.active : ""}`}
              onClick={() => toggleFilter('N')}
            >
              <Checkbox checked={filters.N} />
              <Text className={style.badge_text} text="Network Hospital (N)" />
            </div>
            <div
              className={`${style.badge} ${filters.ESIC ? style.active : ""}`}
              onClick={() => toggleFilter('ESIC')}
            >
              <Checkbox checked={filters.ESIC} />
              <Text className={style.badge_text} text="ESIC Hospital" />
            </div>

          </div>


          {/* <Badge count={"N"} className={style.badge} /> */}
          {component && (
            <Container>
              <ShareProfileButton
                onClick={handleClick}
                btnText={isBtnLoading ? "Loading" : "Send Copy"}
              />
            </Container>
          )}

          <Input
            placeholder="Search Hospitals"
            value={searchValue}
            searchValue={setSearchValue}
            className={style.input}
          />
        </Container>

        {!isSearching && <Text className={style.question} text={displayMessage} />}

        {isSearching ? (
          <Text className={style.question} text="Searching..." />
        ) : displayHospitals.length > 0 ? (
          <Container
            className={component ? `${style.addressCard} ${style.general_enq}` : style.addressCard}
          >
            {displayHospitals.map((hospital, index) => (
              <div
                key={index}
                style={{ height: "100%", width: "100%", display: "flex" }}
              >
                <AddressCard
                  count={index + 1}
                  leftSideIcon="/images/hospital.png"
                  rightSideBadgeText={[ ...(hospital?.isGovernmentHospital ? ["Gov"] : [])]}
                  address={hospital}
                  handleEmergency={getBadgeText(hospital, insurerNamesSet) || []}
                  handleAddressSelection={handleAddressSelection}
                  selectedAddress={selectedHospital}
                  pickupLocation={pickupLocation}
                />
              </div>
            ))}
          </Container>
        ) : (
          null
          // <Text className={style.question} text="No Hospitals Found" />
        )}
      </Container>
    </BackgroundWrapper>
  );
};
